import { property, supertypeClass } from '@haventech/amorphic';
import { Everything } from '@hit/amorphic-types';
import { Policy } from './Policy';
import { Utils } from '../Utils';
import { Agent } from './Agent';

export enum PolicyAgentType {
  PRIMARY = 'primary',
  ADDITIONAL = 'additional',
  SERVICING = 'servicing',
}

@supertypeClass({ toClient: true, toServer: true })
export class PolicyAgent extends Everything /** SplitPartyCoreMixin(Everything)*/ {
  static create = function (policy: Policy, agent: Agent): PolicyAgent {
    return PolicyAgent.new(policy, agent);
  };

  // Logic was included in EventorUtils.buildAdvisor to handle 0/null cases for ratio. If this is ever updated to remove those cases, also update that function
  //@TODO copied from SplitPartyCore
  @property({ rule: ['required', 'numeric'], comment: 'Commission split ratio' })
  ratio: number;

  //@TODO copied from SplitPartyCore
  @property({ isLocal: true })
  ratioChanged: boolean;

  //@TODO copied from SplitPartyCore
  @property()
  partyProperty: string;

  //@TODO copied from SplitPartyCore
  computeRatios(parties, evenSplit) {
    let variableCount = 0;
    let fixedPercent = 0;
    let evenPercent = 0;
    let ix;

    if (evenSplit) {
      evenPercent = Utils.round((1 / parties.length) * 100, 2);

      for (ix = 0; ix < parties.length; ix++) {
        parties[ix].ratio = evenPercent;
      }

      return;
    }

    // See how many slots not changed by user
    for (let ix = 0; ix < parties.length; ++ix) {
      if (parties[ix].ratioChanged) {
        fixedPercent += Number(parties[ix].ratio) * 1;
      } else {
        variableCount++;
      }
    }

    // If we are stuck between a rock and a hard place reset all but current
    if (!variableCount || fixedPercent > 100) {
      for (let ix = 0; ix < parties.length; ++ix) {
        if (parties[ix] !== this || parties.length === 1) {
          parties[ix].ratioChanged = false;
        }
      }
    }

    // See how much of the percent is fixed because user touched it
    fixedPercent = 0;
    variableCount = 0;

    for (let ix = 0; ix < parties.length; ++ix) {
      if (parties[ix].ratioChanged) {
        fixedPercent += Number(parties[ix].ratio) * 1;
      } else {
        variableCount++;
      }
    }

    // Split up the remainder and distribute evenly (round to two decimal places)
    let split = (100 - Math.min(100, fixedPercent)) / variableCount;

    split = Math.floor(split * 100) / 100;

    for (let ix = 0; ix < parties.length; ++ix) {
      if (!parties[ix].ratioChanged) {
        parties[ix].ratio = split;
      }
    }
  }

  /* Set by PRD/DST service*/
  @property({ comment: 'Primary, additional, or servicing agent' })
  type: string;

  @property()
  licenseID: string;

  /* Agent role*/
  @property({
    validate: 'notEmpty()',
    values: function () {
      return this === this.policy.primaryPolicyAgent
        ? ['Soliciting', 'Soliciting_Servicing']
        : ['Producer', 'Servicing_Producer', 'Financial_Alliance', 'Strategic_Alliance', 'Third_Party_Administrator'];
    },
    descriptions: {
      Soliciting: 'Soliciting',
      Soliciting_Servicing: 'Soliciting/Servicing',
      Producer: 'Producer',
      Servicing_Producer: 'Servicing Producer',
      Financial_Alliance: 'Financial Alliance',
      Strategic_Alliance: 'Strategic Alliance',
      Third_Party_Administrator: 'Third party Administrator',
    },
  })
  role: string;

  @property({ getType: () => Agent })
  agent: Agent;

  @property({ rule: ['required', 'numeric'] })
  renewalCommissionRatio: number;

  @property({ getType: () => Policy })
  policy: Policy;

  @property()
  corpId: string;

  @property()
  agencyId: string;

  @property({ comment: 'Stringified data for the corp/agency selected for this policy' })
  corpAgency: string;

  @property({ comment: 'Date when the agent is added to the policy' })
  startDate: Date;

  @property({ comment: 'Date when the agent is removed from the policy' })
  endDate: Date;

  constructor() {
    super();
  }

  static new(
    policy?,
    agent?,
    ratio?: number,
    renewalCommissionRatio?: number,
    agencyId?: string,
    corpId?: string,
    corpAgency?: string
  ) {
    return new PolicyAgent().init(policy, agent, ratio, renewalCommissionRatio, agencyId, corpId, corpAgency);
  }

  init(
    policy?,
    agent?,
    ratio?: number,
    renewalCommissionRatio?: number,
    agencyId?: string,
    corpId?: string,
    corpAgency?: string
  ) {
    if (policy) {
      this.policy = policy;
    }

    if (agent) {
      this.agent = agent;
    }

    this.ratio = ratio;
    this.renewalCommissionRatio = renewalCommissionRatio;
    this.startDate = new Date();

    if (agencyId) {
      this.agencyId = agencyId;
    }

    if (corpId) {
      this.corpId = corpId;
    }

    if (corpAgency) {
      this.corpAgency = corpAgency;
    }

    return this;
  }

  get primaryAgent() {
    return this.agent;
  }
}
